import { html, nothing, LitElement, unsafeCSS, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import SlotController from "@/common/controllers/slot";
import { when } from "@/common/directives/when";
import styles from "@/index.css?inline";
import EkoElement from "@/utilities/element";
import { createSlotChangeHandler, syncSlottedAttributes } from "@/utilities/slot";
import style from "./button.css?inline";
import "@/components/spinner";
import "@/components/icon";

type SlottedElement = "eko-icon" | null;

@customElement("eko-button")
class Button extends EkoElement {
  static formAssociated = true;
  // Turbo will disable the slotted submit button during form submission, so we
  // set loading true when disabled is set on the slotted button.
  // https://turbo.hotwired.dev/reference/attributes#automatically-added-attributes
  static observedSlotAttributes = ["disabled", { disabled: "loading" }];
  static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };

  static styles = [...EkoElement.styles, unsafeCSS(styles), unsafeCSS(style)];

  private buttonSlot = new SlotController(this, "button");

  private disconnectSlottedObserver = () => {
    if (this.slottedButtonObserver) {
      this.slottedButtonObserver.disconnect();
    }
  };

  private handleSlotChange = createSlotChangeHandler(this, {
    after: "afterElement",
    before: "beforeElement",
  });

  private handleSlottedClick = (event: Event) => {
    this.submitButton(event);
  };

  private handleSlottedKeydown = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      this.submitButton(event);
    }
  };

  private internals: ElementInternals;

  private onFocusIn = (e: FocusEvent) => {
    const target = e.target as Element;
    this.toggleAttribute("focus-visible", target.matches(":focus-visible"));
  };

  private onFocusOut = () => {
    this.removeAttribute("focus-visible");
  };

  private slottedButtonObserver: globalThis.MutationObserver | null = null;

  @state() private slottedIsNonFocusable = false;

  @state() afterElement: SlottedElement = null;
  @property({ reflect: true, type: Boolean }) autofocus = false;
  @state() beforeElement: SlottedElement = null;
  @property({ reflect: true, type: Boolean }) disabled = false;
  @property({ reflect: true, type: Boolean }) download = false;
  @property({ reflect: true, type: Boolean }) expand = false;
  @property({ reflect: true }) form?: string;
  @property({ reflect: true }) href?: string;
  @property({ reflect: true, type: Boolean }) loading = false;
  @property({ reflect: true }) name?: string;
  @property({ reflect: true }) size?: "sm" | "md" | "lg"; // default is "md"
  @property({ type: Boolean }) slotted = false;
  @property({ reflect: true, type: Boolean }) square = false;
  @property({ type: Number }) tabindex = 0;
  @property() target?: "_self" | "_blank" | "_parent" | "_top";
  @property({ reflect: true }) theme: "default" | "danger" = "default";
  @property({ type: String, attribute: "data-turbo-action" }) turboAction?: string;
  @property({ type: String, attribute: "data-turbo-confirm" }) turboConfirm?: string;
  @property({ type: String, attribute: "data-turbo-frame" }) turboFrame?: string;
  @property({ type: String, attribute: "data-turbo-method" }) turboMethod?: string;
  @property({ type: String, attribute: "data-turbo-stream" }) turboStream?: string;
  @property({ reflect: true }) type?: "button" | "reset" | "submit";
  @property({ reflect: true }) value?: string;

  @property({ reflect: true }) variant?: "default" | "primary" | "plain"; // default is "default"

  constructor() {
    super();
    this.internals = this.attachInternals();
  }

  private checkSlottedFocus() {
    this.slottedIsNonFocusable = this.slottedSubmitter?.getAttribute("tabindex") === "-1";
  }

  private handleClick() {
    if (this.disabled || this.loading) {
      return;
    }

    if (this.type === "button") {
      return;
    }

    if (this.type === "reset") {
      this.internals.form?.reset();
      return;
    }

    this.internals.form?.requestSubmit();
  }

  private renderButton(content: TemplateResult) {
    return html`
      <button
        class="eko-button"
        part="button root"
        ?autofocus=${this.autofocus}
        ?disabled=${this.disabled}
        ?download=${this.download}
        form=${when(this.form)}
        name=${when(this.name)}
        value=${when(this.value)}
        ?loading=${this.loading}
        type=${when(this.type)}
        @click=${this.handleClick}
        tabindex=${this.tabindex}>
        ${content}
      </button>
    `;
  }

  private renderButtonSlot(content: TemplateResult) {
    return html`<span
      class="eko-button"
      part="button root"
      tabindex=${this.slottedIsNonFocusable ? this.tabindex : -1}
      ?autofocus=${this.slottedIsNonFocusable && this.autofocus}
      @click=${this.handleSlottedClick}
      @keydown=${this.handleSlottedKeydown}>
      ${content}
    </span>`;
  }

  private renderLink(content: TemplateResult) {
    return html`
      <a
        class="eko-button"
        part="button root"
        ?disabled=${this.disabled}
        ?download=${this.download}
        ?loading=${this.loading}
        href=${when(this.disabled, nothing, this.href)}
        rel=${when(this.target === "_blank", "noopener noreferrer")}
        data-turbo-action=${when(this.turboAction)}
        data-turbo-confirm=${when(this.turboConfirm)}
        data-turbo-frame=${when(this.turboFrame)}
        data-turbo-method=${when(this.turboMethod)}
        data-turbo-stream=${when(this.turboStream)}
        target=${ifDefined(this.target)}
        tabindex=${this.tabindex}>
        ${content}
      </a>
    `;
  }

  // Handle form submission when button slot is used.
  // The eko-button element will receive click and keyboard events,
  // this handler ensures the correct associated form is submitted.
  private submitButton(event: Event) {
    let submitter: HTMLButtonElement | HTMLInputElement | null = null;
    let form = this.querySelector("form"); // Rails button_to wraps the button in a form

    if (form) {
      submitter = form.querySelector("button, input[type='submit']");
    } else {
      submitter = this.slottedSubmitter;
      form = submitter?.form || submitter?.closest("form") || null;
    }

    if (!submitter || event.composedPath().includes(submitter)) {
      return;
    }

    if (this.disabled || this.loading) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    if (form) {
      form.requestSubmit(submitter);
    } else {
      submitter.click();
    }
  }

  connectedCallback(): void {
    super.connectedCallback();

    if (this.slotted) {
      this.addEventListener("focusin", this.onFocusIn);
      this.addEventListener("focusout", this.onFocusOut);
    }

    syncSlottedAttributes(
      this,
      this.slottedButtonObserver,
      this.observedElement,
      Button.observedSlotAttributes
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    if (this.slotted) {
      this.removeEventListener("focusin", this.onFocusIn);
      this.removeEventListener("focusout", this.onFocusOut);
    }
    this.disconnectSlottedObserver();
  }

  firstUpdated(): void {
    this.checkSlottedFocus();
  }

  render() {
    const content = html`
      <slot name="before" @slotchange=${this.handleSlotChange}></slot>
      <span class="eko-button__content" part="content">
        <slot
          name=${this.buttonSlot.name}
          @slotchange=${() => {
            this.slottedButtonObserver = syncSlottedAttributes(
              this,
              this.slottedButtonObserver,
              this.observedElement,
              Button.observedSlotAttributes
            );
            this.checkSlottedFocus();
          }}></slot>
        <slot @slotchange=${this.handleSlotChange}></slot>
      </span>
      <eko-spinner
        class="eko-button__spinner"
        part="spinner"
        color="currentColor"
        ?hidden=${!this.loading || Boolean(this.href)}
        size=${this.size ?? "md"}></eko-spinner>
      <slot name="after" @slotchange=${this.handleSlotChange}></slot>
    `;

    if (this.slotted) {
      return this.renderButtonSlot(content);
    } else if (this.href) {
      return this.renderLink(content);
    } else {
      return this.renderButton(content);
    }
  }

  get observedElement() {
    return this.slottedSubmitter;
  }

  private get slottedSubmitter(): HTMLButtonElement | HTMLInputElement | null {
    return this.querySelector?.("button, input[type='submit']") ?? null;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-button": Button;
  }
}

export default Button;
